import { Component, Input, OnInit, OnDestroy, OnChanges } from '@angular/core';

import { HttpErrorResponse } from '@angular/common/http';
import { filter, finalize, take, takeWhile } from 'rxjs/operators';

import { Person } from '../../models/person.model';
import { PersonIdentityService } from '../../services/person-identity.service';
import { LayoutService } from '../../../layouts/layout.service';
import { PasswordUpdate } from '../../models/password-update.model';
import { SecurityQuestionService } from '../../services/security-question.service';
import { SecurityQuestion } from '../../models/security-question.model';
import {
  IdentityProvider,
  isLegacyIdentityProviderMismatch,
  resolveIdentityProvider,
} from '../../models/identity-provider.model';
import { AdminIdentityService } from '../../../Identity/services/admin-identity.service';
import {
  AUTH0_PASSWORD_RESET_FAILED_MESSAGE,
  AUTH0_PASSWORD_RESET_SENT_MESSAGE,
  jxpErrorMessage,
} from '../../../Identity/models/admin-identity-messages';
import { AppConfigurationService } from '../../../shared/services/app-configuration.service';
import { FeatureName } from '../../../shared/enums/feature-name';
import { AuthService } from '../../../shared/auth.service';

@Component({
  selector: 'app-person-identity',
  templateUrl: './person-identity.component.html',
  styleUrls: ['./person-identity.component.scss'],
  standalone: false
})
export class PersonIdentityComponent implements OnInit, OnChanges, OnDestroy {

  @Input() person: Person;

  public componentActive = true;
  public isLoading = false;
  public generatedPassword: string;
  public userHasEmail: boolean;

  /**
   * True once Zing.Identity.Auth0AdminTooling has been read as on for the person on screen. An
   * unreadable flag counts as off, so no reset is offered.
   */
  public auth0ToolingEnabled = false;

  /**
   * All Security Questions which are existing on platform.
   */
  public securityQuestions: Array<SecurityQuestion> = new Array<SecurityQuestion>();

  /**
   * Security Questions which User selected.
   */
  public usersSecurityQuestions: Array<SecurityQuestion> = new Array<SecurityQuestion>();

  /**
   * Display value for the person's current identity provider during the Zing -> Auth0
   * migration (JXP-1479). See resolveIdentityProvider for the fallback and casing rules, which
   * are shared with the Details tab rather than restated here.
   */
  get currentIdentityProviderDisplay(): string {
    return resolveIdentityProvider(this.person?.currentIdentityProvider);
  }

  /**
   * True when the person no longer authenticates through Zing. The password and
   * security-question actions on this tab operate against the legacy Zing identity server
   * (PersonIdentityService, backed by environment.identityApiUrl) and have no effect on an
   * account that signs in elsewhere, so reps need a clear signal before using them.
   */
  get isLegacyIdentityActionMismatch(): boolean {
    return isLegacyIdentityProviderMismatch(this.person?.currentIdentityProvider);
  }

  /**
   * True when the tab offers an Auth0 password reset email through the JXP API. Auth0 lets the
   * user choose their own password from the link, so the representative never handles one. Needs
   * the flag on, a JXP API in this environment, and a provider of exactly Auth0: an unrecognised
   * provider gets no reset, because there is nowhere known to send it.
   */
  get routesPasswordToAuth0(): boolean {
    return this.auth0ToolingEnabled && this.isAuth0Person(this.person);
  }

  get isViewOnly(): boolean {
    return this.authService.isViewOnly;
  }

  constructor(
    private personIdentityService: PersonIdentityService,
    private layoutService: LayoutService,
    private securityQuestionService: SecurityQuestionService,
    private adminIdentityService: AdminIdentityService,
    private appConfigurationService: AppConfigurationService,
    private authService: AuthService,
  ) { }

  ngOnInit() {
    this.getSecurityQuestions();
  }

  ngOnChanges() {
    this.userHasEmail = !!this.person.email;

    if (this.person.referenceId) {
      this.getUserSecurityQuestions(this.person.referenceId);
    }

    this.loadAuth0ToolingFlag();
  }

  ngOnDestroy() {
    this.componentActive = false;
  }

  /**
   * Generates password automatically.
   *
   * Deliberately not routed by provider (JXP-1910): AccountController.GeneratePassword returns a
   * candidate string and writes nothing, so it is provider-agnostic and correct for an Auth0
   * person as-is. Only the actions that persist a credential need a guard.
   */
  public generatePassword() {
    this.personIdentityService.generatePassword()
      .pipe(
        takeWhile(() => this.componentActive)
      )
      .subscribe(
        password => {
          this.generatedPassword = password;
        },
        () => {
          this.layoutService.showUIMessage('Generating password failed, please try again later.');
        }
      );
  }

  /**
   * Updates Person's password.
   *
   * @param userId Person reference Id.
   * @param newPassword New password for Person.
   */
  public updatePassword(password: PasswordUpdate) {
    // JXP-1910: refuse instead of calling. The legacy Zing server retains a credential record for
    // a migrated person, so UpdatePassword finds it, succeeds, and returns Ok(true) while the
    // password the customer actually signs in with is untouched. The JXP-1479 banner warned about
    // that but left the action available, which is worse than not offering it.
    if (this.isLegacyIdentityActionMismatch) {
      this.layoutService.showUIMessage(`This person signs in through ${this.currentIdentityProviderDisplay}.`
        + ' Updating the password here would only change the legacy Zing identity, so it was not attempted.');

      return;
    }

    this.isLoading = true;

    this.personIdentityService.updatePassword(this.person.referenceId, password.newPassword, password.sendEmail)
      .pipe(
        takeWhile(() => this.componentActive)
      ).subscribe(
        res => {
          this.isLoading = false;

          res
            ? this.layoutService.showUIMessage('You have successfully updated a password.')
            : this.layoutService.showUIMessage('Updating password failed, please try again later.');
        },
        err => {
          this.isLoading = false;

          err.error && err.error.description
            ? this.layoutService.showUIMessage(err.error.description)
            : this.layoutService.showUIMessage('Updating password failed, please try again later.');
        }
      );
  }

  /**
   * Has Auth0 email the person a password reset through the JXP API. Auth0 sends the email itself
   * and controls the link and its expiry, so nothing about them reaches this screen.
   */
  public sendAuth0PasswordReset() {
    if (!this.routesPasswordToAuth0) {
      return;
    }

    this.isLoading = true;

    this.adminIdentityService.sendPasswordReset(this.person.id)
      .pipe(
        take(1),
        takeWhile(() => this.componentActive),
        finalize(() => this.isLoading = false)
      ).subscribe(
        () => this.layoutService.showUIMessage(AUTH0_PASSWORD_RESET_SENT_MESSAGE),
        (error: HttpErrorResponse) => this.layoutService.showUIMessage(
          jxpErrorMessage(error, AUTH0_PASSWORD_RESET_FAILED_MESSAGE)));
  }

  /**
   * Return all SecurityQuestion instances.
   *
   * Skipped entirely for a person who no longer signs in through Zing (JXP-1911). The panel that
   * consumes the catalogue is hidden for that population, so the request would only fetch data
   * nothing renders. The guard lives here rather than at the call sites because ngOnChanges runs
   * again whenever a different person is selected.
   */
  public getSecurityQuestions() {
    if (this.isLegacyIdentityActionMismatch) {
      return;
    }

    this.isLoading = true;

    this.securityQuestionService.getSecurityQuestions()
      .pipe(
        takeWhile(() => this.componentActive)
      ).subscribe(
        res => {
          this.isLoading = false;
          this.securityQuestions = res['items'];
        },
        () => {
          this.isLoading = false;
          this.layoutService.showUIMessage('Getting security questions failed. Please try again later.');
        }
      );
  }

  /**
   * Get Security Questions which User selected.
   *
   * Skipped for a person who no longer signs in through Zing (JXP-1911), for the same reason as
   * getSecurityQuestions: their answers live only in the legacy store and the panel is hidden.
   *
   * @param userId Reference id of Person.
   */
  public getUserSecurityQuestions(userId: string) {
    if (this.isLegacyIdentityActionMismatch) {
      return;
    }

    this.isLoading = true;

    this.personIdentityService.getUsersSecurityQuestions(userId)
      .pipe(
        takeWhile(() => this.componentActive)
      ).subscribe(
        res => {
          this.isLoading = false;
          this.usersSecurityQuestions = res;
        },
        () => {
          this.isLoading = false;
          this.layoutService.showUIMessage('Getting security questions for User failed. Please try again later.');
        }
      );
  }

  /**
   * Update current User's security questions.
   *
   * @param changedQuestions Updated security questions with User Id for which questions are updated.
   */
  public changeSecurityQuestions(changedQuestions) {
    const updateObject = {
      userId: this.person.referenceId,
      securityQAs: changedQuestions
    };
    this.isLoading = true;

    this.personIdentityService.changeSecurityQuestions(updateObject)
      .pipe(
        takeWhile(() => this.componentActive)
      ).subscribe(
        res => {
          this.isLoading = false;

          res
            ? this.layoutService.showUIMessage('Updating security questions was successful.')
            : this.layoutService.showUIMessage('Updating security questions failed. Please try again later.');
        },
        () => {
          this.isLoading = false;
          this.layoutService.showUIMessage('Updating security questions failed. Please try again later.');
        });
  }

  private loadAuth0ToolingFlag() {
    this.auth0ToolingEnabled = false;

    const person = this.person;
    if (!this.isAuth0Person(person) || !this.adminIdentityService.isAvailable) {
      return;
    }

    this.appConfigurationService.getFeatureFlag(FeatureName.IdentityAuth0AdminTooling)
      .pipe(
        take(1),
        filter(enabled => enabled === true),
        takeWhile(() => this.componentActive)
      ).subscribe(() => {
        if (this.person === person) {
          this.auth0ToolingEnabled = true;
        }
      });
  }

  private isAuth0Person(person?: Person | null): boolean {
    return !!person && resolveIdentityProvider(person.currentIdentityProvider) === IdentityProvider.Auth0;
  }
}
